import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { authService, auth } from '../services/firebase';

// Définition du type pour le contexte
type AuthContextType = {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  logout: () => Promise<boolean>;
};

// Création du contexte avec une valeur par défaut
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

// Props pour le provider
type AuthProviderProps = {
  children: ReactNode;
};

// Composant Provider pour envelopper l'application
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Effet pour surveiller les changements d'état d'authentification
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      setCurrentUser(user);
      setLoading(false);
    });

    // Nettoyage lors du démontage du composant
    return unsubscribe;
  }, []);

  // Fonctions d'authentification
  const login = async (email: string, password: string) => {
    return await authService.login(email, password);
  };

  const register = async (email: string, password: string) => {
    return await authService.register(email, password);
  };

  const logout = async () => {
    return await authService.logout();
  };

  // Valeur à fournir au contexte
  const value = {
    currentUser,
    loading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
